import { BaseTable } from "@/components/shared/data-table";
import { DataTablePagination } from "@/components/shared/tables/DataTablePagination";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { ChevronsRight, FileUp, Import, Plus } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CreateCounterpartyModal } from "../create-counterparty-modal";
import { ImportCounterpartiesModal } from "../import-counterparties-modal";
import { TableActionBar } from "./action-bar";
import { CounterpartyEditForm } from "./edit-form";
import { CounterpartyStatusFilter } from "./status-filter";
import type { CounterpartiesTableInstance } from "./use-counterparties-table";

export function Table({
  table,
  type,
  showImportButton,
  clickableColumns,
  columnVisibility,
  columns,
  data,
  loading,
  globalFilter,
  setGlobalFilter,
  statusFilterValues,
  onStatusFilterChange,
  onDelete,
  onExportCsv,
  onExportDatev,
  onUpdate,
  selectedSupplier,
  onSelectSupplier,
  onCreated,
  onImported,
}: CounterpartiesTableInstance) {
  const { t } = useTranslation();

  const tableRef = useRef(null);

  const handleRowClick = (event: React.MouseEvent, row: any, index: number) => {
    onSelectSupplier(row.id);
  };

  const drawerIsOpen = selectedSupplier !== null;
  const closeDrawer = useCallback(() => {
    onSelectSupplier(null);
  }, [onSelectSupplier]);

  useEffect(() => {
    const handle = (event: KeyboardEvent) => {
      if (event.key === "Escape" && drawerIsOpen) {
        closeDrawer();
      }
    };
    document.addEventListener("keyup", handle);
    return () => {
      document.removeEventListener("keyup", handle);
    };
  }, [drawerIsOpen, closeDrawer]);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  return (
    <div className="flex flex-col h-full gap-4">
      <Drawer
        direction="right"
        open={drawerIsOpen}
        data-vaul-no-drag
        closeThreshold={1}
        dismissible={false}
      >
        <DrawerPortal data-vaul-no-drag>
          <DrawerOverlay
            className="fixed inset-0 bg-black/40"
            data-vaul-no-drag
          />
          <DrawerContent
            data-vaul-no-drag
            aria-describedby={undefined}
            onInteractOutside={(event) => {
              if (document.activeElement instanceof HTMLElement) {
                // We might have pending changes in an input field, save happens on blur
                document.activeElement.blur();
              }
              closeDrawer();
            }}
            className="bg-white z-50 flex flex-col rounded-t-[10px] h-full w-[700px] mt-24 fixed bottom-0 right-0 max-h-screen min-h-screen overflow-y-auto overflow-x-hidden"
          >
            <div data-vaul-no-drag className="p-5 bg-white flex-1 h-full">
              <div className="max-w-full mx-auto mr-3 ml-3">
                <DrawerTitle className="text-xl font-semibold pb-4">
                  {
                    {
                      creditor: t("creditors.title"),
                      debtor: t("debtors.title"),
                    }[type]
                  }
                  : {selectedSupplier ? selectedSupplier.supplierName : ""} (
                  {selectedSupplier ? selectedSupplier.datevAccountNumber : ""})
                </DrawerTitle>
                {selectedSupplier && (
                  <CounterpartyEditForm
                    data-vaul-no-drag
                    type={type}
                    supplier={selectedSupplier}
                    onUpdate={onUpdate}
                  />
                )}
                <Button
                  onClick={closeDrawer}
                  size="icon"
                  className="hidden h-8 w-8 p-0 m-2 lg:flex bg-transparent hover:bg-black/10 text-muted-foreground"
                  style={{ position: "absolute", top: 0, left: 0 }}
                >
                  <ChevronsRight />
                </Button>
              </div>
            </div>
          </DrawerContent>
        </DrawerPortal>
        <div className="flex items-center gap-2">
          <Input
            placeholder={t("search_creditors_placeholder")}
            value={globalFilter}
            onChange={(event) => setGlobalFilter(event.target.value)}
            className="max-w-sm"
          />
          <CounterpartyStatusFilter
            values={statusFilterValues}
            onChange={onStatusFilterChange}
          />
          <div className="flex-1" />
          {showImportButton && (
            <Button
              onClick={() => setIsImportModalOpen(true)}
              className="text-muted-foreground"
              variant="outline"
            >
              <FileUp className="h-4 w-4 mr-2" />
              {t("suppliers.import.title")}
            </Button>
          )}
          <Button onClick={() => setIsCreateModalOpen(true)} variant="default">
            <Plus className="h-4 w-4 mr-2" />
            {type === "creditor"
              ? t("creditors.create.title")
              : t("debtors.create.title")}
          </Button>
        </div>
        <CreateCounterpartyModal
          type={type}
          open={isCreateModalOpen}
          onOpenChange={setIsCreateModalOpen}
          onSuccess={onCreated}
        />
        <ImportCounterpartiesModal
          open={isImportModalOpen}
          onOpenChange={setIsImportModalOpen}
          onSuccess={onImported}
        />
        <BaseTable
          ref={tableRef}
          loading={loading}
          table={table}
          clickableColumns={clickableColumns}
          columnVisibility={columnVisibility}
          columns={columns}
          data={data}
          onRowClick={handleRowClick}
        />
        <DataTablePagination table={table} disablePagination />
        <TableActionBar
          type={type}
          table={table}
          onDelete={onDelete}
          onExportCsv={onExportCsv}
          onExportDatev={onExportDatev}
        />
      </Drawer>
    </div>
  );
}
